import axios from 'axios';
import _ from 'lodash';
import {
  clearCache as clearClientlessCache,
  getAccessToken,
  getRefreshToken,
} from 'helpers/auth.clientless';
import isEmail from 'validator/lib/isEmail';
import { getCookie, removeCookie } from 'helpers/Storage';
import { apiErrorHandler } from 'helpers/ApiHelper';
import { stripAuthProperties } from 'helpers/AuthHelper';
import { format, FORMAT_ISO, stringToDate, startOfDay } from 'helpers/DateTimeHelper';

const COOKIE_NAME = '__Secure-FOXACCT';
const SSO_COOKIE_NAME = 'FOXSSOACCT';
const getGenderOptions = language => {
  const {
    profileCreate_genderOptionFemale: optionFemale,
    profileCreate_genderOptionMale: optionMale,
    profileCreate_genderOptionOther: optionOther,
  } = language;
  return [
    {
      name: optionMale,
      value: 'm',
    },
    {
      name: optionFemale,
      value: 'f',
    },
    {
      name: optionOther,
      value: 'o',
    },
  ];
};

export const passwordErrors = value => {
  if (!value) return 'You must provide a valid password';
  if (value.length < 6) return 'Password needs to be 6 or more characters long';
  return null;
};
export const isValidPassword = value => !passwordErrors(value);
export const isValidEmail = value => !!(value && isEmail(value));
export const onEnterKeyPress = (e, action) => {
  if (e.key === 'Enter' && action) {
    action();
  }
};

export function convertTimestamp(unixTimestamp) {
  if (!unixTimestamp) return null;
  return new Date(unixTimestamp);
}

export function getLegacyDomain() {
  const url = _.get(window, 'location.hostname', '');
  const urlItems = url.split('.');
  return `.${urlItems.slice(-3).join('.')}`;
}

export function getAccountCookie() {
  return getCookie({ name: SSO_COOKIE_NAME });
}

export function isAnonymousAccount(account) {
  return account?.accountType === 'anonymous';
}

export function getAnonymousAccount() {
  const account = getAccountCookie();
  if (isAnonymousAccount(account)) return account;
  return null;
}

export function getAccount(isAnonymous) {
  return isAnonymous ? null : getAccountCookie();
}

export function removeLegacyAccountCookie() {
  return removeCookie({
    key: COOKIE_NAME,
    options: { path: '/', secure: true, sameSite: true },
  });
}
export function handleError(error, req) {
  const { route } = req;
  const message =
    _.get(error, 'response.data.message') || error.message || _.get(error, 'request.data.message');
  const errorCode = _.get(error, 'response.data.errorCode', null);
  const detail = _.get(error, 'response.data.detail');
  if (!route || !message)
    return { error: { message: 'Sorry, we have encountered a problem!', errorCode } };
  console.error(`Could not get data for route ${route}`, message);
  return { error: { message, errorCode, detail } };
}

export async function getMVPDS(profile) {
  const itemsPerPage = '999'; // Number of items per page
  let response;
  try {
    response = await profile?.getMvpds?.({ itemsPerPage });
  } catch (error) {
    apiErrorHandler({ error });
  }
  return response;
}

export function getCurrentAuthData() {
  const account = getAccountCookie();
  const profileId = account?.profileId;
  const currentAccessToken = account?.accessToken;
  const refreshToken = getRefreshToken(account);

  return {
    authData: account,
    profileId,
    accessToken: currentAccessToken,
    refreshToken,
  };
}

export async function getAccountRegCode(req) {
  const { profile, deviceId } = req;
  const isRegister = false; // if the user should register or login
  const isMVPD = false; // if you want a mvpd login or just account login
  const selectedMvpdId = null; // pre-selected mvpd
  let result;
  try {
    const { code } = await profile.getAccountRegCode({
      deviceId,
      isRegister,
      isMVPD,
      selectedMvpdId,
    });
    result = code;
  } catch (error) {
    apiErrorHandler({ error });
  }

  return result;
}

export function anonymousLogin(setUserAuthData, deviceId, profile) {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await profile?.loginAnonymously({
        deviceId,
      });
      const authorizationToken = getAccessToken(response);
      const account = stripAuthProperties(response);
      if (authorizationToken) {
        setUserAuthData({
          anonymousAccount: account,
          account: null,
          authorizationToken,
        });
        clearClientlessCache();
        resolve({ data: account });
      }
    } catch (error) {
      apiErrorHandler({ error });
      reject(new Error(error));
    }
  });
}

export const getFavorites = ({ route, headers }) =>
  new Promise((resolve, reject) => {
    axios
      .get(route, headers)
      .then(response => {
        resolve(response.data);
      })
      .catch(error => {
        reject(new Error(error));
      });
  });

export const setFavorites = ({ favoritableItems, profileFavorites, isPersonality = false }) =>
  new Promise((resolve, reject) => {
    const favoriteArray = [];
    favoritableItems.forEach(async item => {
      const { id, '@type': type } = item;
      favoriteArray.push({ id: `${type}:${id}` });
      try {
        const response = await profileFavorites.create({
          favorites: favoriteArray,
          domains: ['Nation', 'Entertainment', 'foxnation'],
        });
        resolve(response);
      } catch (error) {
        reject(new Error(error));
      }
    });
  });

export const deleteFavorites = ({ favoritableItems, profileFavorites }) =>
  new Promise(async (resolve, reject) => {
    try {
      await profileFavorites.remove({ favorites: favoritableItems });
      resolve(true);
    } catch (error) {
      reject(new Error(error));
    }
  });

export function toggleAccountExpired() {
  const accountExpired = this.state;
  this.setState({ accountExpired: !accountExpired }, () => true);
  return false;
}

export function getAccountGender({ account, language }) {
  const genderOptions = getGenderOptions(language);
  const validGenders = _.map(genderOptions, 'value');
  const accountGender = _.get(account, 'gender', null);
  return _.includes(validGenders, accountGender) ? accountGender : null;
}

export function getBirthdateIsoFormat(date) {
  const monthStr = date.substring(0, 2);
  const dayStr = date.substring(2, 4);
  const yearStr = date.substring(4);
  const isoFormat = `${yearStr}-${monthStr}-${dayStr}`;
  const isFullDate = isoFormat.length === 10;
  return isFullDate ? isoFormat : null;
}

export function getBirthdateIso8106UTCFormat(date) {
  if (date.length !== 8) return null;

  const monthStr = date.substring(0, 2);
  const dayStr = date.substring(2, 4);
  const yearStr = date.substring(4);
  const parsedDate = `${yearStr}-${monthStr}-${dayStr}`;

  const iso8106UTC = format(startOfDay(stringToDate(parsedDate)), FORMAT_ISO);

  return iso8106UTC;
}
